import { memo } from 'react'
import { DEFAULT_AVATAR, parseAvatarConfig } from './avatar-catalog'
import { AvatarHair } from './AvatarHair'
import { AvatarFace } from './AvatarFace'
import { AvatarClothing } from './AvatarClothing'
import { AvatarAccessories } from './AvatarAccessories'
import { avatarWithCustomBases, hidesAvatarBase } from './avatar-custom'
import { AvatarCustomLayers } from './AvatarCustomLayers'

type Props = {
  config?: unknown
  className?: string
  portrait?: boolean
  label?: string
  transparent?: boolean
  throwAngle?: number
}

/** Original layered SVG, rendered as React nodes. Never interpolates SVG markup. */
export const DonorAvatar = memo(function DonorAvatar({
  config,
  className,
  portrait = false,
  label,
  transparent = false,
  throwAngle,
}: Props) {
  const c = avatarWithCustomBases(parseAvatarConfig(config) ?? DEFAULT_AVATAR)
  const face =
    c.face === 'square'
      ? 'M68 101Q68 57 120 57Q172 57 172 101V157Q172 192 143 194H97Q68 192 68 157Z'
      : c.face === 'oval'
        ? 'M71 102Q71 55 120 55Q169 55 169 102V143Q169 184 120 201Q71 184 71 143Z'
        : 'M65 105Q65 56 120 56Q175 56 175 105V145Q175 193 120 197Q65 193 65 145Z'
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={portrait ? '20 12 200 220' : '0 0 240 280'}
      className={className}
      style={transparent ? undefined : { backgroundColor: c.background }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      width="240"
      height="280"
      overflow={throwAngle === undefined ? undefined : 'visible'}
    >
      {!transparent && <rect width="240" height="280" fill={c.background} />}
      {!transparent && <circle cx="120" cy="124" r="99" fill="#FFF" opacity=".24" />}
      <AvatarCustomLayers config={c} categories={['background']} />
      {c.headwear !== 'hijab' && !hidesAvatarBase(c, 'hair') && (
        <AvatarHair hair={c.hair} hairColor={c.hairColor} back />
      )}
      <AvatarClothing config={c} throwAngle={throwAngle} />
      <AvatarCustomLayers config={c} categories={['clothing', 'clothingColor']} />
      <g fill={c.skin} visibility={hidesAvatarBase(c, 'face') ? 'hidden' : undefined}>
        <ellipse cx="65" cy="128" rx="14" ry="19" />
        <ellipse cx="175" cy="128" rx="14" ry="19" />
      </g>
      <g
        fill="#492514"
        opacity=".16"
        visibility={hidesAvatarBase(c, 'face') ? 'hidden' : undefined}
      >
        <ellipse cx="63" cy="130" rx="5" ry="9" />
        <ellipse cx="177" cy="130" rx="5" ry="9" />
      </g>
      {!hidesAvatarBase(c, 'face') && <path d={face} fill={c.skin} />}
      <path
        d="M72 155q9 31 48 35 39-4 48-35-5 40-48 42-43-2-48-42"
        fill="#492514"
        opacity=".07"
        visibility={hidesAvatarBase(c, 'face') ? 'hidden' : undefined}
      />
      <AvatarCustomLayers config={c} categories={['face', 'skin']} />
      <AvatarFace config={c} />
      <AvatarCustomLayers
        config={c}
        categories={['eyes', 'eyeColor', 'brows', 'mouth', 'detail', 'facialHair']}
      />
      {c.headwear !== 'hijab' && !hidesAvatarBase(c, 'hair') && (
        <AvatarHair hair={c.hair} hairColor={c.hairColor} />
      )}
      <AvatarCustomLayers config={c} categories={['hair', 'hairColor']} />
      <AvatarAccessories config={c} />
      <AvatarCustomLayers
        config={c}
        categories={[
          'earrings',
          'accessory',
          'accessoryColor',
          'headwear',
          'headwearColor',
        ]}
      />
    </svg>
  )
})
